import {
	addAppointment,
	AppointmentDayOfWeek,
	AppointmentDetailModel,
	AppointmentDetailViewModel,
	AppointmentIdentifier,
	AppointmentLocationSaveModel,
	AppointmentRecurrencePatternKind,
	AppointmentRecurrenceRangeKind,
	AppointmentWeekIndex,
	AttendeeSaveModel,
	CreateCalendarAppointmentModel,
	getAppointmentDetails,
	getViewModel,
	RecurrenceOptionsModel,
	RecurrencePatternModel,
	RecurrenceRangeModel,
	RecurringAppointmentOperationKind,
	updateCalendarAppointment,
	UpdateCalendarAppointmentModel
} from "client/Api/Calendar/calendar";
import {
	AlertBar,
	Dialog,
	Form,
	FormItem,
	RichTextEditor,
	RichTextEditorIntermediateStateHandlers,
	Select,
	SelectOption,
	SuggestedMultiComboBoxItem,
	TextInput,
	Toggle,
	Toolbar,
	useConfirmationDialog,
	useRichTextEditorIntermediateState,
	ValidationError
} from "client/Components";
import {
	addDays,
	addTime,
	calculateDayDifference,
	formatForDto,
	getDayOfMonth,
	getStartOfDay
} from "client/DateAndTime/dateTimeUtils";
import { getNextHalfOrWholeHour } from "client/DateAndTime/dateUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { setClientId } from "client/Misc/clientId";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import {
	hasMinimumEntries,
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ComposeAppointmentDialog.module.less";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { SortDirection } from "client/Api/common";
import { AttendeesInput, DisplayContactModelWithoutId } from "../AttendeesInput";
import { DateSelect } from "../DateSelect";
import { IValueInputAttachable } from "client/Forms/attachable";
import { PathRedirect, ReplacementPattern } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import {
	RecurrencePatternKind,
	CalendarAppointmentFormModel,
	CalendarItem,
	CustomRecurrenceOptionsModel,
	CustomRecurrencePatternKind,
	AppointmentLocationFormData
} from "../common";
import { never } from "client_legacy/ndaw/errors";
import { DeleteOrCancelButton } from "../DeleteOrCancelButton";
import { LocationSelectItem } from "../LocationSelectItem";
import { HttpRequestError } from "client/Api/httpClient";
import {
	convertAppointmentDetailsToFormData,
	convertRecurrencePatternKindFormModelToRecurrencePattern
} from "../CalendarContent/ComposeAppointmentBox/ComposeAppointmentBox";
import { getDisplayFriendlyDelay } from "client/DateAndTime/culture";
import { getAppointmentDayOfWeek, getWeekIndexInMonth } from "../CalendarSidebarContent/RepeatOptionSelector/RecurrencePatternKindSelector";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

type ComposeAppointmentDialogCreateProps = {
	initialData?: CalendarAppointmentFormModel;
	appointmentIdentifier?: undefined;
	recurringOperationKind?: undefined;
};

type ComposeAppointmentDialogUpdateProps = {
	appointmentIdentifier?: AppointmentIdentifier;
	recurringOperationKind?: RecurringAppointmentOperationKind;
	initialData?: undefined;
};

export type ComposeAppointmentDialogProps = {
	onClose: (savedCalendarId?: readonly string[]) => void;
	timezone: TimeZone;
} & (ComposeAppointmentDialogCreateProps | ComposeAppointmentDialogUpdateProps);

export const ComposeAppointmentDialog = (props: ComposeAppointmentDialogProps): ReactElement => {
	const {
		onClose,
		timezone,
		initialData,
		appointmentIdentifier,
		recurringOperationKind: recurringOperationKindFromProps
	} = props;

	const stableAppointmentIdentifier = useSynchronizedRef(appointmentIdentifier);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [calendars, setCalendars] = useState<null | readonly CalendarItem[]>(null);
	const [appointmentDetailsView, setAppointmentDetailsView] =
		useState<AppointmentDetailModel | null>(null);
	const [wasRecurrenceMessageDismissed, setWasRecurrenceMessageDismissed]
		= useState<boolean>(false);
	const [formData, setFormData] = useState<CalendarAppointmentFormModel>(
		(): CalendarAppointmentFormModel => {
			if (isNotNil(initialData)) {
				return initialData;
			}

			const startTime: Date = getNextHalfOrWholeHour(new Date(), timezone);
			const endTime = calendarAppointmentFormDefaultData.isAllDay
				? startTime
				: addTime(
					startTime,
					30,
					"minute"
				);

			return {
				...calendarAppointmentFormDefaultData,
				startTime,
				endTime
			};
		}
	);

	const isFirstAttendeeAddedRef: RefObject<boolean> = useRef<boolean>(false);

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<CalendarAppointmentFormModel> => {

			let result: IValidationConfiguration<CalendarAppointmentFormModel> = staticAppointmentValidation;

			result = result.clone();

			result.when(
					x => !isEndTimeValid(
						x.startTime,
						x.endTime,
						x.isAllDay,
						timezone
					)
				)
				.forScalar("endTime")
				.add(
					inline((): InputValidationResult => {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
						};
					})
				);

			result
				.forObject("recurrenceOptions")
				.when(x => x.recurrencePatternKind === RecurrencePatternKind.Custom)
				.forObject("customRecurrenceOptions")
				.when(x => x.kind === CustomRecurrencePatternKind.Weekly)
				.forObject("weeklyCustomRecurrenceOptions")
				.forCollection("daysOfWeek")
				.add(
					hasMinimumEntries(1, localize("_AtLeastOneOptionMustBeSelected")),
					isNotNullNorUndefined(),
					isNotEmpty()
				);

			result
				.forObject("recurrenceOptions")
				.when(x => x.recurrencePatternKind !== RecurrencePatternKind.DontRepeat)
				.forObject("range")
				.when(x => x.kind === AppointmentRecurrenceRangeKind.EndDate)
				.forScalar("endDate")
				.add(
					isNotNullNorUndefined()
				);

			result
				.when(
					x => {
						const { range, recurrencePatternKind } = x.recurrenceOptions;

						return recurrencePatternKind !== RecurrencePatternKind.DontRepeat
							&& range.kind === AppointmentRecurrenceRangeKind.EndDate
							&& !isEndTimeValid(
								x.startTime,
								range.endDate,
								x.isAllDay,
								timezone
							);
					}
				)
				.forObject("recurrenceOptions")
				.forObject("range")
				.when(x => x.kind === AppointmentRecurrenceRangeKind.EndDate)
				.forScalar("endDate")
				.add(
					inline((): InputValidationResult => {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
						};
					})
				);
			return result;
		},
		[timezone]
	);

	const formHandler: IFormHandler<CalendarAppointmentFormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const richTextEditorState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();
	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const isEdit: boolean = isNotNil(initialData?.id) || isNotNil(appointmentIdentifier);

	const calendarsOptions = useMemo(
		(): readonly SelectOption<string>[] => {
			return sortBy(
				calendars ?? [],
				x => x.isDefault,
				SortDirection.Descending
			)
				.filter(x => x.canEdit)
				.map(
					(x: CalendarItem): SelectOption<string> => ({
						text: x.name,
						value: x.id
					})
				);
		},
		[calendars]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			let serverValidationPathRedirects: readonly PathRedirect[] | undefined = undefined;

			const validation: IRootValidation<CalendarAppointmentFormModel> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				if (formData.title === "") {
					const result: TryResult<void> = await openConfirmationDialog({
						title: localize("_AreYouSureYouWantToSaveThisAppointmentWithoutATitle"),
						confirmText: localize("_Save"),
						trackingId: "save-appointment-without-a-title-confirmation-dialog"
					});

					if (!result.wasSuccessful) {
						setIsBusy(false);
						return;
					}
				}

				if (isNil(formData?.id)) {
					serverValidationPathRedirects = [
						{
							pattern: new DotPathWildcardPattern("ReminderMinutes.*?"),
							replacement: "ReminderMinutes"
						},
						{
							pattern: new DotPathWildcardPattern("IsPrivate.*?"),
							replacement: "IsPrivate"
						},
						{
							pattern: new DotPathWildcardPattern("RecurrenceOptions.Pattern.Kind"),
							replacement: "RecurrenceOptions.RecurrencePatternKind"
						},
						{
							pattern: new DotPathWildcardPattern("RecurrenceOptions.Pattern.RepeatInterval"),
							replacement: "RecurrenceOptions.CustomRecurrenceOptions.RepeatInterval"
						},
						{
							pattern: new DotPathWildcardPattern("RecurrenceOptions.Range.EndDate"),
							replacement: "RecurrenceOptions.Range.EndDate"
						},
						{
							pattern: new AttendeesPathReplacementPattern(),
							replacement: "Attendees"
						}
					];

					await addAppointment(
						convertCalendarAppointmentFormModelToCreateModel(formData, timezone),
						globalCancellation
					);
				} else {
					serverValidationPathRedirects = [
						{
							pattern: new DotPathWildcardPattern("RecurrenceTarget.RecurrenceOptions.**.Pattern.Kind"),
							replacement: "RecurrenceOptions.RecurrencePatternKind"
						},
						{
							pattern: new DotPathWildcardPattern("RecurrenceTarget.RecurrenceOptions.**.Pattern.RepeatInterval"),
							replacement: "RecurrenceOptions.CustomRecurrenceOptions.RepeatInterval"
						},
						{
							pattern: new DotPathWildcardPattern("RecurrenceTarget.RecurrenceOptions.**.Range.EndDate"),
							replacement: "RecurrenceOptions.Range.EndDate"
						},
						{
							pattern: new AttendeesPathReplacementPattern(),
							replacement: "Attendees"
						}
					];

					await updateCalendarAppointment(
						formData.id,
						convertCalendarAppointmentFormModelToUpdateModel(
							formData,
							timezone
						),
						globalCancellation
					);
				}

				alertManager.showSuccess(localize("_SuccessfullySavedTheAppointment"));

				let mutatedCalendarIds: (string[]) | undefined;

				if (isNotNil(formData.calendarId)) {
					if (
						isNotNil(
							initialData?.calendarId
						)
							&& initialData.calendarId !== formData.calendarId
					) {
						mutatedCalendarIds = [
							initialData.calendarId,
							formData.calendarId
						];
					} else {
						mutatedCalendarIds = [
							formData.calendarId
						];
					}
				}

				onClose(mutatedCalendarIds);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e, serverValidationPathRedirects)) {
					console.error(e);

					if (e instanceof HttpRequestError && e.statusCode === 500) {
						alertManager.showError(localize("_AppointmentCantBeSaved"));
					} else {
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			initialData?.calendarId,
			isBusy,
			onClose,
			openConfirmationDialog,
			timezone
		]
	);

	const recurringOperationKind: RecurringAppointmentOperationKind | undefined | null = isNotNil(initialData)
		? initialData.recurringOperationKind
		: recurringOperationKindFromProps;

	const isCalendarSelectDisabled = useMemo(
		(): boolean => {
			if (!isEdit) {
				return false;
			}

			const hasAttendees: boolean = isNotNil(appointmentDetailsView)
				&& appointmentDetailsView.attendees
					.length > 0;

			const isPartialRecurrence: boolean = isNotNil(recurringOperationKind)
				&& recurringOperationKind !== RecurringAppointmentOperationKind.All;

			return isPartialRecurrence || hasAttendees;
		},
		[
			appointmentDetailsView,
			isEdit,
			recurringOperationKind
		]
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): EffectWithDestructorResult => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const { calendars } = await getViewModel(signal);

					const calendarItems: readonly CalendarItem[] = calendars.map(
						(x): CalendarItem => {
							return setClientId({
								...x,
								...(
									isNotNil(x.configuration)
										? {
											isEnabled: true,
											color: x.configuration
												.color
										}
										: {
											isEnabled: false
										}
								)
							});
						}
					);

					setCalendars(calendarItems);

					setFormData(x => ({
						...x,
						calendarId: x.calendarId ?? getSelectedCalendarIdByDefault(calendarItems)
					}));

					const appointmentIdentifierToFetch: AppointmentIdentifier | undefined = initialData?.id && initialData?.calendarId
						? {
							id: initialData.id,
							calendarId: initialData.calendarId
						}
						: stableAppointmentIdentifier.current;

					if (isNil(appointmentIdentifierToFetch)) {
						setIsBusy(false);
						return;
					}

					const viewModel: AppointmentDetailViewModel = await getAppointmentDetails(
						appointmentIdentifierToFetch,
						signal
					);

					const appointment: AppointmentDetailModel = viewModel.model;
					setAppointmentDetailsView(appointment);

					if (isNotNil(stableAppointmentIdentifier.current)) {
						if (isNil(appointment.masterId)
							|| isNil(recurringOperationKind)
							|| recurringOperationKind === RecurringAppointmentOperationKind.Current
						) {
							setFormData(
								convertAppointmentDetailsToFormData(
									appointment,
									recurringOperationKindFromProps,
									calendarItems,
									timezone
								)
							);

							setIsBusy(false);

							return;
						}

						const masterAppointmentsViewModel: AppointmentDetailViewModel = await getAppointmentDetails(
							{
								id: appointment.masterId,
								calendarId: appointment.calendar.id
							},
							signal
						);

						const masterAppointmentsDetails: AppointmentDetailModel = masterAppointmentsViewModel.model;

						setFormData(
							convertAppointmentDetailsToFormData(
								{
									...masterAppointmentsDetails,
									// Include the master appointment data in the form, but use the currently selected appointment ID.
									// This is necessary to identify the starting point for mutating the appointments when selecting "CurrentAndFollowing".
									// The start and end dates should also be the currently selected ones when RecurringAppointmentOperationKind === CurrentAndFollowing
									id: stableAppointmentIdentifier.current.id,
									start: recurringOperationKindFromProps === RecurringAppointmentOperationKind.CurrentAndFollowing
										? appointment.start
										: masterAppointmentsDetails.start,
									end: recurringOperationKindFromProps === RecurringAppointmentOperationKind.CurrentAndFollowing
										? appointment.end
										: masterAppointmentsDetails.end,
								},
								recurringOperationKindFromProps,
								calendarItems,
								timezone
							)
						);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			stableAppointmentIdentifier,
			initialData?.id,
			initialData?.calendarId,
			recurringOperationKind,
			recurringOperationKindFromProps,
			stableOnClose,
			timezone
		]
	);

	const firstAttendeeChangeHandler = useCallback(
		(
			attendees: readonly SuggestedMultiComboBoxItem<DisplayContactModelWithoutId>[]
		): void => {
			if (attendees.length > 0
				&& !isFirstAttendeeAddedRef.current
			) {
				isFirstAttendeeAddedRef.current = true;

				formHandler
					.for("isTeamsMeeting")
					.whenType(
						x => x.isSet,
						x => x.reducer
							.setValue({
								isSet: true,
								value: true
							})
					);
			}
		},
		[formHandler]
	);

	const reminderMinutesValue: OptionalModel<number | null> = formHandler.for("reminderMinutes")
		.value;

	const reminderOptions = useMemo(
		(): readonly SelectOption<number | null>[] => {
			const reminderMinutes: number | null = reminderMinutesValue.isSet
				? reminderMinutesValue.value
				: null;

			if (isNil(reminderMinutes)) {
				return defaultReminderOptions;
			}

			const isValuePresentInDefaultOptions: boolean = isNotNil(
				defaultReminderOptions.find((x => x.value === reminderMinutes))
			);

			if (isValuePresentInDefaultOptions) {
				return defaultReminderOptions;
			}

			const newOption: SelectOption<number> = {
				text: getDisplayFriendlyDelay(reminderMinutes),
				value: reminderMinutes
			};

			const index: number = defaultReminderOptions.findIndex(
				option => option.value !== null && option.value > newOption.value
			);

			if (index === -1) {
				defaultReminderOptions.push(newOption);
			} else {
				defaultReminderOptions.splice(index, 0, newOption);
			}

			return defaultReminderOptions;
		},
		[reminderMinutesValue]
	);

	return (
		<Dialog
			variant="left-slider"
			onClose={onClose}
			hasBusyIndicator={isBusy}
			trackingId="create-calendar-appointment-dialog"
		>
			<Dialog.Header
				title={isEdit ? localize("_EditEvent") : localize("_CreateEvent")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body>
				<Form
					height="fill"
					hasWidthConstrained
					onSubmit={submitHandler}
				>
					{!wasRecurrenceMessageDismissed && isNotNil(recurringOperationKind) && (
						<div className={styles.inlineAlertContainer}>
							<AlertBar
								kind="info"
								isInline
								onDismiss={() => setWasRecurrenceMessageDismissed(true)}
							>
								{localize(
									operationKindToInfoMessageMap[recurringOperationKind]
								)}
							</AlertBar>
						</div>
					)}
					<FormItem
						label={localize("_Calendar")}
					>
						<div className={styles.itemWithToggleContainer}>
							<div className={styles.item}>
								<Select
									{...formHandler.for("calendarId").attachToSelect()}
									options={calendarsOptions}
									menuTitle={localize("_Calendar")}
									isDisabled={isCalendarSelectDisabled}
								/>
								<ValidationError {...formHandler.for("calendarId").attachToValidationError()} />
							</div>
							{formHandler.for("isPrivate").whenType(
								(x) => x.isSet,
								(isPrivateHandler: IValueInputAttachable<SetOptionalModel<boolean>>): ReactElement => (
									<NoUniqueInputIdContext>
										<Toggle
											{...isPrivateHandler.for("value").attachToCheckbox()}
											text={localize("_PrivateEvent")}
										/>
									</NoUniqueInputIdContext>
								)
							)}
						</div>
					</FormItem>

					<FormItem
						label={localize("_Title")}
					>
						<TextInput
							{...formHandler.for("title").attachToTextInput()}
							placeholder={localize("_EnterTitle")}
							autoFocus="focus"
						/>
						<ValidationError {...formHandler.for("title").attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_Attendees")}
					>
						<AttendeesInput
							attachable={formHandler.for("attendees")}
							onAfterAttendeeChange={firstAttendeeChangeHandler}
						/>
						<ValidationError {...formHandler.for("attendees").attachToValidationError()} />
					</FormItem>

					<DateSelect
						attachable={formHandler}
						isMobile={isMobile}
						timezone={timezone}
						hasLabel
						shouldShowRecurrenceOptions={recurringOperationKind !== RecurringAppointmentOperationKind.Current}
						isDontRepeatDisabled={isNotNil(recurringOperationKind)}
					/>

					{formHandler.for("reminderMinutes").whenType(
						x => x.isSet,
						(reminderHandler: IValueInputAttachable<SetOptionalModel<number | null>>): ReactElement => (
							<FormItem
								label={localize("_Reminder")}
							>
								<Select
									{...reminderHandler.for("value").attachToSelect()}
									options={reminderOptions}
									menuTitle={localize("_Reminder")}
								/>
								<ValidationError {...formHandler.for("reminderMinutes").attachToValidationError()} />
							</FormItem>
						)
					)}

					<LocationSelectItem
						attachable={formHandler}
						meeting={appointmentDetailsView?.meeting ?? null}
						timezone={timezone}
						hasLabel
					/>
					<FormItem
						label={localize("_Description")}
					>
						<div className={styles.descriptionContainer}>
							<RichTextEditor
								{...formHandler.for("description").attachToRichTextEditor(richTextEditorState)}
								placeholder={localize("_EnterDescription")}
								height="fill"
							/>
						</div>
						<ValidationError {...formHandler.for("description").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					{isEdit && isNotNil(appointmentDetailsView)
						&& (
							<DeleteOrCancelButton
								onIsBusyChange={setIsBusy}
								onSuccess={() => onClose([
									appointmentDetailsView.calendar
										.id
								])}
								appointmentDetailsView={appointmentDetailsView}
								recurringOperationKind={formData.recurringOperationKind}
							/>
						)
					}
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={submitHandler}
						trackingId="save-button"
					/>
				</Toolbar>
			</Dialog.Footer>
			{confirmationDialog}
		</Dialog>
	);
};
ComposeAppointmentDialog.displayName="ComposeAppointmentDialog";

type ComposeAppointmentDialogInitialData = (
	ComposeAppointmentDialogCreateProps
	| ComposeAppointmentDialogUpdateProps
) & { timezone: TimeZone; };

export function useComposeAppointmentDialog(): [
	element: ReactElement | null,
	open: (
		props: ComposeAppointmentDialogInitialData
	) => Promise<TryResult<readonly string[]>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => {
			const closeHandler = (calendarIds?: readonly string[]): void => {
				if (isNil(calendarIds)) {
					onClose();
				} else {
					onSave(calendarIds);
				}
			};

			return (
				<ComposeAppointmentDialog
					{...props}
					onClose={closeHandler}
				/>
			);
		}
	);
}

export class AttendeesPathReplacementPattern implements ReplacementPattern {
	public tryReplace(path: string, replacement: string): TryResult<string> {
		if (/Attendees\[\d+\]/.test(path)) {
			return {
				wasSuccessful: true,
				result: replacement
			};
		}

		return {
			wasSuccessful: false
		};
	}
}

export const staticAppointmentValidation = createValidationConfiguration<CalendarAppointmentFormModel>();

staticAppointmentValidation.forScalar("calendarId")
	.add(
		isNotNullNorUndefined()
	);

staticAppointmentValidation.forScalar("startTime")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

staticAppointmentValidation.forScalar("endTime")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

export function isEndTimeValid(
	startTime: Date | null,
	endTime: Date | null,
	isAllDay: boolean,
	timezone: TimeZone
): boolean {
	if (isNil(startTime) || isNil(endTime)) {
		return false;
	}

	if (isAllDay) {
		return calculateDayDifference(
			endTime,
			startTime,
			timezone
		) >= 0;
	}

	return !isNaN(startTime.getTime())
		&& !isNaN(endTime.getTime())
		&& endTime > startTime;
};

export function getSelectedCalendarIdByDefault(
	calendars: readonly CalendarItem[]
): string {
	const editableCalendars: readonly CalendarItem[] = calendars.filter((x) => x.canEdit);

	const defaultCalendar: CalendarItem | undefined = editableCalendars.find(c => c.isDefault);

	if (isNotNil(defaultCalendar) && defaultCalendar.isEnabled) {
		return defaultCalendar.id;
	}

	const calendar: CalendarItem | undefined = editableCalendars
		.find(x => x.isEnabled)
		?? editableCalendars[0];

	return ensureNotNil(calendar)
		.id;
};

type CommonFormModel = Omit<
	CreateCalendarAppointmentModel,
	"isPrivate"
		| "reminderMinutes"
		| "recurrenceTarget"
		| "recurrenceOptions"
		| "isTeamsMeeting"
>;

function getCommonFormModelProperties (
	formModel: CalendarAppointmentFormModel,
	timeZone: TimeZone
): CommonFormModel {
	const attendees: AttendeeSaveModel[] = formModel.attendees
		.map(x => ({ emailAddress: x.value.emailAddress }));

	const locations: readonly AppointmentLocationSaveModel[] = formModel
		.locations
		.map(x => convertToAppointmentLocationSaveModel(x.value));

	const startTime: Date | null = prepareStartTimeForSaving(
		formModel.startTime,
		formModel.isAllDay,
		timeZone
	);

	const endTime: Date | null = prepareEndTimeForSaving(
		formModel.endTime,
		formModel.isAllDay,
		timeZone
	);

	return {
		calendarId: ensureNotNil(formModel.calendarId),
		isAllDay: formModel.isAllDay,
		title: formModel.title,
		description: formModel.description,
		attendees,
		locations,
		startTime: ensureNotNil(formatForDto(startTime, timeZone)),
		endTime: ensureNotNil(formatForDto(endTime, timeZone))
	};
}

function convertToAppointmentLocationSaveModel(
	location: AppointmentLocationFormData
): AppointmentLocationSaveModel {
	return isNotNil(location.id)
		? {
			id: location.id,
			displayName: location.displayName
		}
		: {
			displayName: location.displayName
		};
}

export function prepareStartTimeForSaving(
	startTime: Date | null,
	isAllDay: boolean,
	timeZone: TimeZone
): Date | null {
	return isAllDay && isNotNil(startTime)
		? getStartOfDay(startTime, timeZone)
		: startTime;
}

export function prepareEndTimeForSaving(
	endTime: Date | null,
	isAllDay: boolean,
	timeZone: TimeZone
): Date | null {
	return isAllDay && isNotNil(endTime)
		? addDays(
			getStartOfDay(endTime, timeZone),
			1,
			timeZone
		)
		: endTime;
}

export function convertCalendarAppointmentFormModelToCreateModel(
	formModel: CalendarAppointmentFormModel,
	timeZone: TimeZone
): CreateCalendarAppointmentModel {
	const recurrenceOptions: OptionalModel<RecurrenceOptionsModel> = convertFormModelToRecurrenceOptionsModel(
		formModel,
		formModel.startTime ?? new Date(),
		timeZone
	);

	return {
		...getCommonFormModelProperties(formModel, timeZone),
		isPrivate: formModel.isPrivate
			.isSet
				? formModel.isPrivate
					.value
				: false,
		reminderMinutes: formModel.reminderMinutes
			.isSet
				? formModel.reminderMinutes
					.value
				: reminderMinutesMap.fifteenMinutes,
		recurrenceOptions: recurrenceOptions.isSet
			? recurrenceOptions.value
			: null,
		isTeamsMeeting: formModel.isTeamsMeeting
			.isSet
				? formModel.isTeamsMeeting
					.value
				: false
	};
}

export function convertCalendarAppointmentFormModelToUpdateModel(
	formModel: CalendarAppointmentFormModel,
	timeZone: TimeZone,
	hasOptionalProperties: boolean = true
): UpdateCalendarAppointmentModel {
	const { recurrenceOptions } = formModel;
	const shouldRepeatSingleAppointment: boolean = isNil(formModel.recurringOperationKind)
		&& recurrenceOptions.recurrencePatternKind !== RecurrencePatternKind.DontRepeat;

	return {
		...getCommonFormModelProperties(formModel, timeZone),
		isPrivate: hasOptionalProperties ?
			formModel.isPrivate
			: { isSet: false },
		reminderMinutes: hasOptionalProperties ?
			formModel.reminderMinutes
			: { isSet: false },
		isTeamsMeeting: formModel.isTeamsMeeting
			.isSet
				? formModel.isTeamsMeeting
					.value
						? formModel.isTeamsMeeting
						: { isSet: false }
				: { isSet: false },
		recurrenceTarget: isNotNil(formModel?.recurringOperationKind)
			|| shouldRepeatSingleAppointment
				? {
					recurringOperationKind: formModel.recurringOperationKind ?? RecurringAppointmentOperationKind.Current,
					recurrenceOptions: !hasOptionalProperties
						|| formModel.recurringOperationKind === RecurringAppointmentOperationKind.Current
							? { isSet: false }
							: convertFormModelToRecurrenceOptionsModel(
								formModel,
								formModel.startTime ?? new Date(),
								timeZone
							)
				}
				: null
		};
}

function convertFormModelToRecurrenceOptionsModel(
	formModel: CalendarAppointmentFormModel,
	startDate: Date,
	timezone: TimeZone
): OptionalModel<RecurrenceOptionsModel> {
	const {
		range,
		recurrencePatternKind,
		customRecurrenceOptions
	} = formModel.recurrenceOptions;

	if (recurrencePatternKind === RecurrencePatternKind.DontRepeat) {
		return { isSet: false };
	}

	let computedRange: RecurrenceRangeModel;

	switch (range.kind) {
		case AppointmentRecurrenceRangeKind.EndDate:
			computedRange = {
				kind: range.kind,
				endDate: formatForDto(
					ensureNotNil(range.endDate),
					timezone
				),
				startDate: formatForDto(
					startDate,
					timezone
				)
			};
			break;
		case AppointmentRecurrenceRangeKind.NoEnd:
			computedRange = {
				kind: range.kind,
				startDate: formatForDto(
					startDate,
					timezone
				)
			};
			break;
		default:
			never(range.kind);
	}

	const pattern: RecurrencePatternModel = recurrencePatternKind !== RecurrencePatternKind.Custom
		? convertRecurrencePatternKindFormModelToRecurrencePattern(
			recurrencePatternKind,
			startDate,
			timezone
		)
		: convertCustomFormModelToRecurrencePattern(
			customRecurrenceOptions,
			startDate,
			timezone
		);

	return {
		isSet: true,
		value: {
			pattern,
			range: computedRange
		}
	};
}

function convertCustomFormModelToRecurrencePattern(
	customRecurrenceOptions: CustomRecurrenceOptionsModel,
	startDate: Date,
	timezone: TimeZone
): RecurrencePatternModel {
	const {
		repeatInterval,
		weeklyCustomRecurrenceOptions,
		monthlyCustomRecurrenceOptions,
		yearlyCustomRecurrenceOptions
	} = customRecurrenceOptions;

	const dayOfMonth: number = getDayOfMonth(startDate, timezone);
	const weekIndex: AppointmentWeekIndex = getWeekIndexInMonth(startDate, timezone);
	const month: number = startDate.getMonth() + 1;
	const appointmentDayOfWeek: AppointmentDayOfWeek = getAppointmentDayOfWeek(
		startDate,
		timezone
	);

	switch (customRecurrenceOptions.kind) {
		case CustomRecurrencePatternKind.Daily:
			return {
				kind: AppointmentRecurrencePatternKind.Daily,
				repeatInterval
			};
		case CustomRecurrencePatternKind.Weekly:
			return {
				kind: AppointmentRecurrencePatternKind.Weekly,
				daysOfWeek: weeklyCustomRecurrenceOptions.daysOfWeek,
				repeatInterval
			};
		case CustomRecurrencePatternKind.Monthly:
			return monthlyCustomRecurrenceOptions.kind === "absolute"
				? {
					kind: AppointmentRecurrencePatternKind.AbsoluteMonthly,
					dayOfMonth,
					repeatInterval
				}
				: {
					kind: AppointmentRecurrencePatternKind.RelativeMonthly,
					daysOfWeek: [appointmentDayOfWeek],
					weekIndex,
					repeatInterval
				};
		case CustomRecurrencePatternKind.Yearly:
			return yearlyCustomRecurrenceOptions.kind === "absolute"
				? {
					kind: AppointmentRecurrencePatternKind.AbsoluteYearly,
					dayOfMonth,
					monthIndex: month,
					repeatInterval
				}
				: {
					kind: AppointmentRecurrencePatternKind.RelativeYearly,
					daysOfWeek: [appointmentDayOfWeek],
					weekIndex,
					monthIndex: month,
					repeatInterval
				};
		default:
			never(customRecurrenceOptions.kind);
	}
}

export const reminderMinutesMap = {
	dontRemindMe: null,
	atTimeOfTheEvent: 0,
	fiveMinutes: 5,
	tenMinutes: 10,
	fifteenMinutes: 15,
	halfAnHour: 30,
	oneHour: 60,
	twoHours: 60 * 2,
	twelveHours: 60 * 12,
	oneDay: 60 * 24,
	oneWeek: 60 * 24 * 7,
};

export const calendarAppointmentFormDefaultData: CalendarAppointmentFormModel = {
	attendees: [],
	locations: [],
	calendarId: null,
	description: "",
	startTime: null,
	endTime: null,
	isAllDay: false,
	title: "",
	isPrivate: { isSet: true, value: false },
	reminderMinutes: { isSet: true, value: reminderMinutesMap.fifteenMinutes },
	isTeamsMeeting: { isSet: true, value: false },
	recurringOperationKind: undefined,
	recurrenceOptions: {
		recurrencePatternKind: RecurrencePatternKind.DontRepeat,
		customRecurrenceOptions: {
			repeatInterval: 1,
			kind: CustomRecurrencePatternKind.Weekly,
			weeklyCustomRecurrenceOptions: {
				daysOfWeek: []
			},
			monthlyCustomRecurrenceOptions: {
				kind: "absolute"
			},
			yearlyCustomRecurrenceOptions: {
				kind: "absolute"
			}
		},
		range: {
			kind: AppointmentRecurrenceRangeKind.EndDate,
			endDate: null
		}
	}
};

export const operationKindToInfoMessageMap: {
	[key in RecurringAppointmentOperationKind]: LocalizationKey;
} = {
	[RecurringAppointmentOperationKind.All]: "_YouAreEditingAllEventsInTheSeries",
	[RecurringAppointmentOperationKind.CurrentAndFollowing]: "_YouAreEditingThisAndAllFollowingEventsInTheSeries",
	[RecurringAppointmentOperationKind.Current]: "_YouAreEditingOneEventInTheSeries"
};

export const defaultReminderOptions: SelectOption<number | null>[] = [
	{ text: localize("_DontRemindMe"), value: reminderMinutesMap.dontRemindMe },
	{ text: localize("_AtTimeOfTheEvent"), value: reminderMinutesMap.atTimeOfTheEvent },
	{ text: localize("_5Minutes"), value: reminderMinutesMap.fiveMinutes },
	{ text: localize("_10Minutes"), value: reminderMinutesMap.tenMinutes },
	{ text: localize("_15Minutes"), value: reminderMinutesMap.fifteenMinutes },
	{ text: localize("_30Minutes"), value: reminderMinutesMap.halfAnHour },
	{ text: localize("_1Hour"), value: reminderMinutesMap.oneHour },
	{ text: localize("_2Hours"), value: reminderMinutesMap.twoHours },
	{ text: localize("_12Hours"), value: reminderMinutesMap.twelveHours },
	{ text: localize("_1Day"), value: reminderMinutesMap.oneDay },
	{ text: localize("_1Week"), value: reminderMinutesMap.oneWeek }
];